// THE WEBSITES' STATE IN AN AI ANSWER — the `project-status` view
// (functions/canvasAnswer.js canvasAiViews; components/ProjectStatusView.js).
// Nika 2026-10-18: "fly should cover all functionality that would be possible
// otherwise" — "Is our site live?" is answered by this card (QA 2026-10-18).
//
// Two modes, one card:
//   every website  no project named and no website on screen: more than one
//                  website, or a project on screen that is not a website (an
//                  app canvas — its only website never stands in for it):
//                  each website (the first SITES_MAX, in canvasAnswerSites'
//                  order) with its production state chip — live, never
//                  published, not available. QA on dev 2026-10-19: "What is
//                  the status of our sites?" composed ONE site's card beside
//                  a projects list with no state, so "Never published"
//                  (content-and-marketing, bi-app) stood nowhere on the page.
//                  A row opens that site's detail in the card; Back returns.
//   one website    a project named (the params, or the card's own row pick),
//                  the website on screen, or (nothing on screen) the
//                  workspace's only website.
//
// Read-only. One website's three reads, into the view's OWN state:
//   live      what the production site's ADDRESS serves — the project's
//             environments and its published release, read as a member (the
//             preview package's previewSiteHosting + previewEnvServes): live,
//             never published (it serves the latest saved version), not
//             available (switched off); the public data read
//             (previewSiteRead) only when the environments cannot be read,
//             else unknown. Never the project's visibility: the render worker
//             serves a private project's site to every visitor (dev
//             2026-10-19: system/invest read "Private" while its address
//             answered 200 to an anonymous visitor).
//   envs      what each environment serves (the same environments): off, the
//             published release (or never published), a pinned version, the
//             latest of a branch
//   versions  the newest versions of the main branch (`sdk.getProjectVersions`,
//             canvas functions/versionHistory.js's list call)
// Publishing is never this card's: a change the ask asks for is the page's
// "Do it" (the assistant's Tools, with its confirm card — docs/FLY_MODE.md).
//
// Frank note: el.call serializes each export on its own (FA204) — every export
// is self-contained.

// The view's read, once per params, the card's own pick, the installed apps
// and the project on screen. `{ site, sites, total, live, envs, versions,
// failed, none, notWebsite, storeApp }` in its own state: `sites` (the
// every-website rows, each `{ key, name, owner, live }`) or `site` (one
// website), never both; `notWebsite` / `storeApp` the name the one sentence
// says when the asked name is no website.
export async function canvasStatusLoad(el) {
  const host = el || this
  if (!host || !host.state || typeof host.state.update !== 'function') return null
  const params = (host.scope && host.scope.params) || {}
  const root = host.state.root || {}
  const installed = host.call('canvasAnswerInstalled', root)
  const onScreen = host.call('canvasAnswerOnScreen', root)
  const limit = Math.max(1, Math.min(10, Math.floor(Number(params.limit)) || 5))
  const pick = typeof host.state.pick === 'string' ? host.state.pick.trim() : ''
  const named = pick || (typeof params.project === 'string' ? params.project.trim() : '')
  // A list that landed after the card answered (the project list, the App
  // Store list) can change what the name means: canvasAnswerNamedMoved decides
  // again and moves the counter in this signature when the answer moved.
  const moved = host.call('canvasAnswerNamedMoved', host, named)
  const sig = JSON.stringify([
    named,
    limit,
    installed,
    onScreen ? onScreen.owner + '/' + onScreen.key : '',
    moved
  ])
  if (host.__canvasStatusSig === sig) return null
  host.__canvasStatusSig = sig
  const read = await host.call('canvasAnswerRead', host)
  if (host.__canvasStatusSig !== sig) return null
  const sites = host.call('canvasAnswerSites', read && read.projects, installed)
  const settle = (p) =>
    Promise.resolve(p).then(
      (r) => r,
      () => null
    )
  const base = (k) => String(k || '').split('.')[0]
  const shown =
    !named && onScreen && onScreen.key
      ? sites.find(
          (x) =>
            base(x.key) === base(onScreen.key) &&
            (!onScreen.owner || !x.owner || x.owner === onScreen.owner)
        )
      : null
  // A project on screen that is not one of the websites (an app canvas): the
  // card never puts the only website in its place, in silence (dev
  // 2026-10-31, the Velvet Lantern canvas) — it lists every website.
  const away = !named && !shown && !!(onScreen && onScreen.key)
  // EVERY WEBSITE: no name and no website on screen — more than one website,
  // or any website at all beside a project on screen that is not one.
  if (!named && !shown && (sites.length > 1 || (away && sites.length > 0))) {
    const SITES_MAX = 8
    const rows = sites.slice(0, SITES_MAX)
    host.state.update({
      site: null,
      sites: rows.map((r) => ({ key: r.key, name: r.name, owner: r.owner, live: null })),
      total: sites.length,
      live: null,
      envs: null,
      versions: null,
      failed: false,
      none: false,
      notWebsite: null,
      storeApp: null
    })
    const reads = await Promise.all(
      rows.map((r) =>
        settle(host.call('previewSiteHosting', { owner: r.owner, key: r.key, id: r.id || null }))
      )
    )
    if (host.__canvasStatusSig !== sig) return null
    host.state.update({
      sites: rows.map((r, i) => ({
        key: r.key,
        name: r.name,
        owner: r.owner,
        live: host.call(
          'canvasStatusLive',
          host.call('previewEnvServes', reads[i], 'production'),
          null
        )
      }))
    })
    return rows
  }
  // ONE WEBSITE — what the name means (canvasAnswerNamed, the rule of every
  // website card): a website; else a project of this workspace that is not a
  // website, or an App Store app that is no project here — the one sentence
  // names it, never "no match" (items 1, 2, 3 — 2026-10-31). Its load step
  // canvasAnswerNamedRead reads the App Store list once when nothing matches
  // and the shell has not read it yet.
  const meant =
    (await host.call(
      'canvasAnswerNamedRead',
      sites,
      read && !read.failed ? read.projects : null,
      named,
      onScreen,
      root
    )) || {}
  if (host.__canvasStatusSig !== sig) return null
  const site = meant.kind === 'website' ? meant.site : null
  if (!site) {
    host.state.update({
      site: null,
      sites: null,
      live: null,
      envs: null,
      versions: null,
      failed: true,
      none: !named && !!read && !read.failed && !sites.length,
      notWebsite: meant.kind === 'project' ? meant.name : null,
      storeApp: meant.kind === 'storeApp' ? meant.name : null
    })
    return null
  }
  host.state.update({
    site: { key: site.key, name: site.name, owner: site.owner },
    sites: null,
    live: null,
    envs: null,
    versions: null,
    failed: false,
    none: false,
    notWebsite: null,
    storeApp: null
  })
  const sdk = host.sdk || host.call('getSDK')
  const data = (v) => (v && typeof v === 'object' && 'data' in v && v.success ? v.data : v)
  const hostingRead = settle(
    host.call('previewSiteHosting', { owner: site.owner, key: site.key, id: site.id || null })
  )
  const versionsOf = (id) =>
    id && sdk && typeof sdk.getProjectVersions === 'function'
      ? sdk
          .getProjectVersions(id, {
            branch: 'main',
            page: 1,
            limit,
            fields: 'value,message,createdAt,user,versionNumber'
          })
          .then(data, () => null)
      : Promise.resolve(null)
  const versionRead = site.id ? versionsOf(site.id) : hostingRead.then((h) => versionsOf(h && h.id))
  const liveRead = settle(
    host.call('previewSiteRead', { owner: site.owner, key: site.key, env: 'production' })
  )
  const [hosting, versionRes, liveRes] = await Promise.all([hostingRead, versionRead, liveRead])
  if (host.__canvasStatusSig !== sig) return null
  const serves = {}
  for (const env of ['production', 'staging', 'development']) {
    serves[env] = host.call('previewEnvServes', hosting, env)
  }
  host.state.update({
    live: host.call('canvasStatusLive', serves.production, liveRes),
    envs: host.call('canvasStatusEnvs', hosting && hosting.known ? hosting.environments : null, {
      production: serves.production === 'never' || !!(liveRes && liveRes.never),
      staging: serves.staging === 'never',
      development: serves.development === 'never'
    }),
    versions: host.call('canvasStatusVersions', versionRes, limit)
  })
  return site
}

// The production site's state for the house status chip, from what its
// address serves (`serves` — previewEnvServes over previewSiteHosting): live
// (success) when it serves a release, a pinned version or the newest of a
// branch; never published (warning — it serves the latest saved version); not
// available (danger) when production is switched off. When the environments
// cannot be read: the public data read's answer (`read` — previewSiteRead:
// live, never), else unknown. The project's visibility is never the site's.
export function canvasStatusLive(serves, read) {
  const r = read && typeof read === 'object' && !read.error ? read : null
  if (serves === 'off') return { status: 'danger', label: 'canvas.status.live.unavailable' }
  if (serves === 'never' || (r && r.never)) {
    return { status: 'warning', label: 'canvas.status.live.never' }
  }
  if (serves) return { status: 'success', label: 'canvas.status.live.live' }
  if (r && r.published) return { status: 'success', label: 'canvas.status.live.live' }
  return { status: null, label: 'canvas.status.live.unknown' }
}

// Each environment, in the publish order: `{ key, label, serves, version,
// branch, on }` — `label` and `serves` are bundle keys (the version or branch
// fills its `{value}`). An environment the project has no config for, or one
// switched off, serves nothing.
// `never` (optional): `{ <env>: true }` for a `published`-mode environment on
// a project never published — it reads so, not "Published release".
export function canvasStatusEnvs(map, never) {
  const envs = map && typeof map === 'object' ? map : null
  if (!envs) return null
  const nothing = never && typeof never === 'object' ? never : {}
  const LABELS = {
    production: 'canvas.sidebar.env.production',
    staging: 'canvas.sidebar.env.staging',
    development: 'canvas.sidebar.env.development'
  }
  return ['production', 'staging', 'development'].map((key) => {
    const cfg = envs[key] && typeof envs[key] === 'object' ? envs[key] : null
    const on = !!cfg && cfg.enabled !== false
    const branch = (cfg && typeof cfg.branch === 'string' && cfg.branch) || 'main'
    const version = (cfg && typeof cfg.version === 'string' && cfg.version) || ''
    let serves = 'canvas.status.env.off'
    let value = ''
    if (on && cfg.mode === 'version' && version) {
      serves = 'canvas.status.env.version'
      value = version
    } else if (on && cfg.mode === 'published') {
      serves = nothing[key] ? 'canvas.status.env.never' : 'canvas.status.env.published'
    } else if (on && cfg.mode === 'branch') {
      serves = 'canvas.status.env.branch'
      value = branch
    } else if (on) {
      serves = 'canvas.status.env.latest'
      value = branch
    }
    return { key, label: LABELS[key], serves, value, on }
  })
}

// The newest versions as rows: `{ key, value, message, at, author }` — `at`
// the ISO time (the row reads it through the house `timeAgo`), `author` a
// name or ''. Keyed by the version id (a row keyed by its value would become
// a tag named like it — never here, ids are opaque).
export function canvasStatusVersions(res, limit) {
  const list = Array.isArray(res)
    ? res
    : res && Array.isArray(res.versions)
      ? res.versions
      : res && Array.isArray(res.items)
        ? res.items
        : null
  if (!list) return null
  const n = Math.max(1, Math.min(10, Math.floor(Number(limit)) || 5))
  return list
    .filter((v) => v && typeof v === 'object' && v.value)
    .slice(0, n)
    .map((v, i) => {
      const who = v.author || v.user || null
      const author =
        who && typeof who === 'object' ? String(who.name || who.username || '').trim() : ''
      return {
        key: 'v-' + String(v._id || v.id || i),
        value: String(v.value),
        message: typeof v.message === 'string' ? v.message.trim().slice(0, 140) : '',
        at: v.createdAt || v.created_at || null,
        author
      }
    })
}

// The card's ↗: the project's canvas (its editor — where versions and
// publishing live); null when no site can be named from the root's list, or
// when the card shows EVERY website (no project named and no website on
// screen: more than one website, or a project on screen that is not one) —
// the view's own route, the Studio's projects, then stands. A name that is an
// App Store app (no project here): its App Store page.
export function canvasStatusLink(params, root) {
  const r = root && typeof root === 'object' ? root : {}
  const p = params && typeof params === 'object' ? params : {}
  const list = this.call('canvasAnswerProjects', r)
  if (!Array.isArray(list)) return null
  const sites = this.call('canvasAnswerSites', list, this.call('canvasAnswerInstalled', r))
  const onScreen = this.call('canvasAnswerOnScreen', r)
  const named = typeof p.project === 'string' ? p.project.trim() : ''
  const base = (k) => String(k || '').split('.')[0]
  const shown =
    !named && onScreen && onScreen.key
      ? sites.find(
          (x) =>
            base(x.key) === base(onScreen.key) &&
            (!onScreen.owner || !x.owner || x.owner === onScreen.owner)
        )
      : null
  // The every-website list (canvasStatusLoad's own rule).
  const away = !named && !shown && !!(onScreen && onScreen.key)
  if (!named && !shown && (sites.length > 1 || (away && sites.length > 0))) return null
  // The card's own rule (canvasAnswerNamed): a project that is not a website
  // links nowhere, as its card shows no site; an App Store app that is no
  // project here links to its App Store page (canvasAnswerStoreLink).
  const meant = this.call('canvasAnswerNamed', sites, list, named, onScreen, r) || {}
  if (meant.kind === 'storeApp') return this.call('canvasAnswerStoreLink', meant.app)
  const site = meant.kind === 'website' ? meant.site : null
  if (!site || !site.owner || !site.key) return null
  return '/' + encodeURIComponent(site.owner) + '/' + encodeURIComponent(site.key.split('.')[0])
}

// A row of the every-website list opens that site in the card (`key`), Back
// (`key` null) returns to every website — the card's OWN choice, read by
// canvasStatusLoad before the model's params.
export function canvasStatusPick(el, key) {
  const view =
    el && el.dataProjectStatus === true
      ? el
      : el && typeof el.lookup === 'function'
        ? el.lookup((e) => e.dataProjectStatus === true)
        : null
  const s = view && view.state
  if (!s || typeof s.update !== 'function') return false
  s.update({ pick: typeof key === 'string' && key ? key : null })
  return true
}
